// Resolvers for links that point at the Astro public-site (a separate
// deployment/origin from this SPA). Mirrors the origin resolution already
// used by feedCardShared.tsx + AccountProfilePage.tsx: prefer the
// VITE_PUBLIC_SITE_ORIGIN build var, else fall back to the current origin.
//
// NB in local `docker compose` the public-site is not part of the stack,
// so these URLs are only fully reachable in a deployed environment. The
// point of routing them here (not at a bare SPA path) is that a shared
// link is a PUBLIC storefront URL — it must not land inside the gated SPA
// (which bounces unknown paths to /feed).

const PUBLIC_SITE_ORIGIN =
  (typeof window !== "undefined" &&
    (import.meta.env.VITE_PUBLIC_SITE_ORIGIN as string | undefined)) ||
  (typeof window !== "undefined" ? window.location.origin : "");

/** Public storefront page for a drop — `${origin}/shop/<drop_id>`.
 *  Resolves to the Astro STOREFRONT origin (VITE_PUBLIC_SITE_ORIGIN): the
 *  `/shop/*` pages are SSG'd there, not routes on this SPA. When that var
 *  is unset the fallback is `window.location.origin` (this SPA host), where
 *  `/shop/<id>` is an unknown path and nginx bounces it to `/feed` — which
 *  is exactly the defect Story 10.6 fixes by SETTING the var in the
 *  Dockerfile + web-deploy pipeline. */
export function publicShopUrl(dropId: string): string {
  return `${PUBLIC_SITE_ORIGIN}/shop/${dropId}`;
}

// Story 10.6 — the /u, /c, /d show-off routes are served by THIS SPA host
// (they are SPA routes, not Astro storefront pages), so their shareable
// URLs resolve to the SPA's OWN origin — deliberately NOT
// VITE_PUBLIC_SITE_ORIGIN, which points at the storefront that serves
// /shop. Using window.location.origin keeps a copied link on the host that
// actually renders these routes.
const SPA_ORIGIN = typeof window !== "undefined" ? window.location.origin : "";

/** A collector's public collection page — `${spaOrigin}/u/<handle>`. */
export function publicCollectionUrl(handle: string): string {
  return `${SPA_ORIGIN}/u/${handle}`;
}

/** A single certificate permalink — `${spaOrigin}/c/<share_id>`. The
 *  DURABLE per-item link (survives handle rename + ownership transfer). */
export function certificatePermalinkUrl(shareId: string): string {
  return `${SPA_ORIGIN}/c/${shareId}`;
}

/** A single sealed-dossier permalink — `${spaOrigin}/d/<dossier_id>`. */
export function sealedDossierPermalinkUrl(dossierId: string): string {
  return `${SPA_ORIGIN}/d/${dossierId}`;
}
